/**
 * Global event emitter
 *
 * Usage:
 *
 * import ge from '@/utilities/GlobalEvents'
 *
 * To listen:
 * 	 ge.on('message', (msg: string) => { ... })
 *
 * To emit:
 *   ge.emit('message', 'Hello, World!')
 *
 * To stop listening (in onUnmounted):
 *   go.off('message', (msg: string) => { ... })
 *
 * Its a good idea to create the event handler as a seperate function so that it can be easily removed.
 */

import type { MediaDevice } from '@/stores/MediaDevices.types'
import mitt from 'mitt'
import type { Emitter } from 'mitt'
import type { SipClientEvent, SipClientRTCSession } from './SipClient.types'
import type { causes } from '@per_moeller/jssip-telecomx/lib/Constants'
import type { PbxAppDashboardItem, PbxAppEmployeesExtensionCallWithDate, PbxAppQueuesAllItem, PbxAppQueuesCaller, PbxAppQueuesMember } from '@/TcxApi/PbxApp'
import type { ValidateTokenExtendedResponse } from '@/TcxApi/Authentication'
import type { ErrorModalMessage, QuestionModalMessage, RecordingTranscribeRequest, ToastMessage } from '@/definitions/Common'
import type Employee from '@/stores/Employee'
import type { Props } from '@/components/dashboard/CallDraggable.vue'
import type { PbxPhonebookContact } from '@/TcxApi/PbxPhonebook'
import type { PbxRecordingItem } from '@/TcxApi/PbxRecording'
import type { PbxVoicemailItem } from '@/TcxApi/PbxVoicemail'
import type { PbxCdrCall } from '@/TcxApi/PbxCdr'

/**
 * Types of events that can be emitted and their data
 */
export type Events = { // eslint-disable-line
	/** When user has successfully logged in */
	'Auth:LoggedIn': ValidateTokenExtendedResponse

	/** When user has been logged out - value is what section did it */
	'Auth:LoggedOut': string

	/** When wakeup after sleep was detected */
	Wakeup: undefined

	/** When the users employee record was updated */
	MeUpdated: string

	/** Employees has been loaded */
	'Employees:Loaded': undefined

	/** Every second */
	OneSecond: undefined
	/** Every minute */
	OneMinute: undefined
	/** When it is a new day (for daily jobs) */
	Midnight: undefined

	/** Invoke common error modal to shoe an error message */
	CommonErrorModal: ErrorModalMessage
	/** Invoke the common question modal */
	CommonQuestionModal: QuestionModalMessage

	/** Request the common question modal to dismiss the displayed modal */
	CommonQuestionModalDismiss: undefined

	/** Show a toast - a short message that goes away by itself, for what needs no answer */
	CommonToast: ToastMessage

	/** When modules report back with status, in response to a StatusRequest */
	StatusReport: { key: string, value: string | number | boolean | null | MediaDevice[] }

	/** When modules are requested to repot their state */
	StatusRequest: undefined

	/** When an audio/video device has been added or removed */
	MediaDeviceChange: MediaDevice & { event: 'added' | 'removed' }

	/** When an audio/video device has been selected */
	MediaDeviceSelected: { type: 'audioinput' | 'audiooutput' | 'videoinput' | 'ringeroutput' | 'ringervolume' }

	/** When SIP client is retrying to connect to the server */
	'SipClient:Error:Retry': undefined

	/** When SIP client gave up on connecting to the server */
	'SipClient:Error:Terminate': undefined

	/** When an event occurred in the SIP client */
	SipClient: { event: SipClientEvent, attempt?: number, code?: number, reason?: string, cause?: causes, session?: SipClientRTCSession }

	/** Request to answer a call */
	'Call:Answer': { sessionId: string }
	/** Request to answer a call by callers number */
	'Call:AnswerByCallerNumber': { callerNumber: string }
	/** Request to hangup a call */
	'Call:Hangup': { sessionId: string }
	/** Request to reject a call */
	'Call:Reject': { sessionId: string }
	/** Request to put a call or other calls on hold */
	'Call:ActivateHold': { sessionId?: string, exceptCall?: string } | undefined
	/** Request to resume a held call */
	'Call:DeactivateHold': { sessionId: string }
	/** Request to resume a held call - internally */
	'Call:DeactivateHoldInternal': { sessionId: string }
	/** Request to transfer a call */
	'Call:Transfer': { originator: PbxAppEmployeesExtensionCallWithDate, receiver: PbxAppEmployeesExtensionCallWithDate }
	/** Request to SIP client to dial a number */
	'Call:Dial': { number: string }

	/** Request by Jabra headset to present webHID */
	'Jabra:PresentWebHid': MediaDevice

	/** When realtime has connected to the server */
	'Realtime:Connected': undefined
	/** When realtime has disconnected from the server */
	'Realtime:Disconnected': undefined

	/** When queues has been loaded */
	'Queues:Loaded': undefined

	/** When a queue has been created */
	'Queues:Created': PbxAppQueuesAllItem
	/** When a queue has been updated */
	'Queues:Updated': PbxAppQueuesAllItem
	/** When a queue has been deleted */
	'Queues:Deleted': PbxAppQueuesAllItem
	/** When a caller enters a queue */
	'Queues:Entered': { queue: string, caller: PbxAppQueuesCaller }
	/** When a caller leaves a queue */
	'Queues:Left': { queue: string, caller: PbxAppQueuesCaller, index: number }
	/** When a queue member joins */
	'Queues:MemberJoin': { queue: string, member: PbxAppQueuesMember }
	/** When a queue member is updated */
	'Queues:MemberUpdate': { queue: string, member: PbxAppQueuesMember, index: number }
	/** When a queue member leaves */
	'Queues:MemberLeft': { queue: string, member: PbxAppQueuesMember, index: number }

	/** Phonebook was reloaded */
	'Phonebook:Reloaded': undefined
	/** When a contact is created */
	'Phonebook:Created': string
	/** When a contact is updated */
	'Phonebook:Updated': string
	/** When a contact has been deleted */
	'Phonebook:Deleted': string
	/** When a new contact shall be created with initial data provided */
	'Phonebook:CreateNew': PbxPhonebookContact | undefined
	/** When duplicate was found during create, this is the - I still want to create event */
	'Phonebook:CreateNewDo': PbxPhonebookContact
	/** When a duplicate was found during create, this is the I want to merge with existing event */
	'Phonebook:AppendToExisting': { existing: PbxPhonebookContact, append: PbxPhonebookContact }
	/** Show the contact in the phonebook with the given id */
	'Phonebook:Show': string
	/** The contact has been closed - signal til phonebooklist that the selected item is no more selected, value is id of contact */
	'Phonebook:Close': string
	/** Request to delete a contact */
	'Phonebook:Delete': string

	/** To inform that a call has been rejected */
	CallRejected: { source: string }

	/** When an attended transfer is requested */
	AttendedTransferRequested: { source: string, destination: string }
	/** When a blind transfer is requested */
	BlindTransferRequested: { source: string, target: string }
	/** Request to MyCalls to auto answer the next call it received with the specified sessionId */
	MyCallsAutoAnswerNextRinging: string

	/** Message from http to settings that a request has failed */
	RequestHasFailed: undefined

	/** Show the set presence modal, value is id of employee */
	CommonSetPresenceModal: Employee

	/** Show the send sms modal */
	CommonSendSmsModal: { employee: Employee | undefined, number: string | undefined }

	/** Show the send group sms modal */
	CommonSendGroupSmsModal: undefined

	/** Show the employee data modal */
	CommonEmployeeDataModal: Employee

	/** show the redirect modal */
	CommonSetRedirectModal: Employee

	/** show the calendar modal */
	CommonCalendarModal: Employee

	/** show the calendar event modal */
	CommonCalendarEventModal: { employee: Employee, eventId?: string, startTime?: Date }

	/** Show the settings modal */
	CommonSettings: undefined | string

	/** Instruct my calls to send DTMF tone, value is the tone to send: 0-9#* */
	MyCallsSendDTMF: string

	/** Set focus to search box */
	FocusSearch: undefined

	/** Enable/disable wallboard mode */
	'Layout:Wallboard': boolean

	/** Event to inform that voicemail counters has been updated */
	'Voicemail:Count': { newMessages: number, oldMessages: number }

	/** A dashboard has been selected */
	'Dashboard:Select': string

	/** User wants to create a new dashboard */
	'Dashboard:Create': undefined

	/** User wants to edit the current dashboard */
	'Dashboard:Edit': undefined

	/** User wants to delete the dashboard specified */
	'Dashboard:Delete': string

	/** Request to delete an item on the active dashboard */
	'Dashboard:DeleteItem': PbxAppDashboardItem

	/** Enable/disable wallboard mode */
	'Dashboard:Wallboard': boolean

	/** Request to auto-generate a dashboard */
	'Dashboard:AutoCreate': undefined

	/** User wants to logout */
	'Auth:InitiateLogout': undefined

	/** User want to show/hide the my-presence contextmenu */
	MyPresenceToggleMenu: undefined

	/** Click on the topbar button at the given position */
	SelectTopbarButton: number

	/** Announcing that a drag has started */
	CallDragStart: Props

	/** Announcing that a drag has ended */
	CallDragEnd: undefined

	/** Invoked to reload the app in an attempt to recover from a network error */
	'Network:Error:Retry': undefined

	/** Request to terminate the app due to fatal network error */
	'Network:Error:Terminate': undefined

	/** User has confirmed that the selected recording shall be deleted */
	'Recording:Delete': PbxRecordingItem

	/** User has confirmed that the selected CDR call recording shall be deleted */
	'CdrRecording:Delete': PbxCdrCall

	/** User has answered whether the selected recording shall be summarized by the AI as well as transcribed */
	'Recording:Transcribe': RecordingTranscribeRequest

	/** User has answered whether the selected CDR call recording shall be summarized by the AI as well as transcribed */
	'CdrRecording:Transcribe': RecordingTranscribeRequest

	/** User has confirmed that the selected voicemail shall be deleted */
	'Voicemail:Delete': PbxVoicemailItem

	/** User has confirmed that all voicemails shall be deleted */
	'Voicemail:DeleteAll': undefined

	/** Request to Pick a phone during launch */
	PickAPhone: undefined

	/** Request to open CDR note editor */
	CdrNoteEditor: PbxAppEmployeesExtensionCallWithDate

	/** Request for the user to initiate WebHID pairing */
	KuandoPairingRequired: undefined

	/** Requests that Context Menues are hidden  */
	HideContextMenu: undefined

	/** Requests the employees handler to reload the users extensions */
	EmployeeReloadMyExtensions: undefined
}

const emitter: Emitter<Events> = mitt<Events>()

export default emitter
